import { parsearCsv, limpiar } from "./conciliacionPagos";

// Lectura de los 3 archivos que Financieros baja de los bancos, tal como salen del portal (sin abrirlos en Excel):
//   - TARJETA: MultiCobros (Banorte) -> Analitica -> Reportes -> "Reporte de Depositos", exportado a CSV.
//              Trae las ventas con TPV de los planteles, los pagos en linea (afiliacion 08916724) y la Pinpad de pruebas.
//   - BANORTE: portal de Banorte, pagos en ventanilla del servicio 05446 (CSV "datos-exportados").
//   - BBVA:    estado de cuenta de BBVA en formato Cuaderno 43 (archivo .exp), con los depositos CIE.
// Cada archivo se convierte en "movimientos" con la misma forma, que es como se guardan en la BD
// (tblmultipagos_banco_movimientos). La clave identifica al movimiento para no guardarlo dos veces.

export const FUENTES = {
  TARJETA: "Tarjeta (TPV y en línea)",
  BANORTE: "Ventanilla Banorte",
  BBVA: "Ventanilla BBVA",
};

export const AFILIACION_EN_LINEA = "08916724";
export const AFILIACION_PINPAD = "07357461"; // Pinpad de Tesistan; hoy solo se usa para pruebas

// Afiliacion de la TPV (A920 Pro) de cada plantel, en orden del plantel oficial 01 a 31
// (las mismas del grupo de afiliaciones dado de alta en MultiCobros).
const AFILIACIONES_TPV = [
  "09804905", "09827603", "09827611", "09826834", "09824976", "09839781", "09838313", "09825541", "09826273", "09826278",
  "09826293", "09826865", "09846282", "09826384", "09826473", "09826390", "09826929", "09838318", "09826966", "09825537",
  "09825540", "09838247", "09826957", "09838320", "09826292", "09826943", "09838329", "09827552", "09834199", "09826969",
  "09827543",
];
export const PLANTEL_DE_AFILIACION = AFILIACIONES_TPV.reduce((m, af, i) => ({ ...m, [af]: String(i + 1).padStart(2, "0") }), {});

const sinAcentos = (s) => String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const redondear = (n) => Math.round(n * 100) / 100;
const numero = (v) => {
  const n = Number(String(v || "0").replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};
// "16/10/2026" -> "2026-10-16"; "01/01/0001" (sin fecha) -> ""
const fechaDeDmy = (v) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(limpiar(v));
  if (!m || m[3] === "0001") return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};
// "261019" (AAMMDD) -> "2026-10-19"
const fechaDeAammdd = (v) => (/^\d{6}$/.test(v) ? `20${v.substring(0, 2)}-${v.substring(2, 4)}-${v.substring(4, 6)}` : "");
// "03:36:22 p. m." / "11:29:41 a. m." / "13:13:23" -> "15:36:22"
const horaDe24 = (v) => {
  const t = limpiar(v).toLowerCase();
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap])?/.exec(t);
  if (!m) return "";
  let h = Number(m[1]);
  if (m[4] === "p" && h < 12) h += 12;
  if (m[4] === "a" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}:${m[3] || "00"}`;
};
const oficialDeReferencia = (ref) => (/^\d{2}/.test(ref) && ref.substring(0, 2) !== "00" ? ref.substring(0, 2) : "");

const indices = (cabecera, columnas, nombreReporte) => {
  const cab = cabecera.map((c) => sinAcentos(limpiar(c)));
  const I = {};
  Object.entries(columnas).forEach(([clave, nombre]) => {
    const i = cab.indexOf(sinAcentos(nombre));
    if (i < 0) throw new Error(`No encuentro la columna "${nombre}" en el ${nombreReporte}.`);
    I[clave] = i;
  });
  return I;
};

// ---------------------------------------------------------------- TARJETA: Reporte de Depositos (MultiCobros)
const COLUMNAS_DEPOSITOS = {
  afiliacion: "Afiliación", estatus: "Estatus de Transacción", tipo: "Tipo transaccion", control: "Número de Control",
  tarjeta: "Tipo de Tarjeta", monto: "Monto de Transacción Signo", fecha: "Fecha Transacción", aut: "Código Autorización",
  ref1: "Referencia Cliente 1", ref2: "Referencia Cliente 2", aplicacion: "Fecha Aplicación", iva: "Total de IVA",
  comisiones: "Total de Comisiones", hora: "Hora de Transacción",
};

function movimientosDeDepositos(tabla) {
  const I = indices(tabla[0], COLUMNAS_DEPOSITOS, "Reporte de Depósitos de MultiCobros");
  const movimientos = [];
  const omitidos = [];
  tabla.slice(1).forEach((crudo) => {
    const r = crudo.map(limpiar);
    if (r.length < tabla[0].length - 1 || !r[I.afiliacion]) return;
    const afiliacion = r[I.afiliacion].padStart(8, "0");
    const tipo = sinAcentos(r[I.tipo]).startsWith("devoluc") ? "DEVOLUCION" : sinAcentos(r[I.tipo]) === "venta" ? "VENTA" : "";
    const importe = numero(r[I.monto]);
    const fecha = fechaDeDmy(r[I.fecha]);
    const hora = horaDe24(r[I.hora]);
    if (!tipo || !fecha || Number.isNaN(importe)) { omitidos.push(`${r[I.tipo] || "sin tipo"} ${r[I.fecha]} ${r[I.monto]}`); return; }
    // Las comisiones y su IVA vienen en negativo en una venta (y en positivo en una devolucion, porque se regresan)
    const comision = redondear(-numero(r[I.comisiones]));
    const iva = redondear(-numero(r[I.iva]));
    let oficial = PLANTEL_DE_AFILIACION[afiliacion] || "";
    if (afiliacion === AFILIACION_EN_LINEA) oficial = oficialDeReferencia(r[I.ref1]);
    if (afiliacion === AFILIACION_PINPAD) oficial = "01";
    movimientos.push({
      fuente: "TARJETA",
      clave: ["T", afiliacion, tipo, fecha, hora, r[I.aut]].join("|"),
      cuenta: afiliacion,
      oficial,
      tipo,
      fecha,
      hora,
      fecha_deposito: fechaDeDmy(r[I.aplicacion]),
      importe: redondear(importe),
      comision,
      iva,
      depositado: redondear(importe - comision - iva),
      autorizacion: r[I.aut],
      referencia: r[I.ref1],
      referencia_tecleada: r[I.ref2],
      folio: r[I.control],
      medio: r[I.tarjeta],
      estatus_banco: r[I.estatus],
    });
  });
  return { movimientos, omitidos };
}

// ---------------------------------------------------------------- BANORTE: ventanilla, servicio 05446
const COLUMNAS_BANORTE = {
  factura: "FACTURA", ref1: "REFERENCIA 1", bruto: "IMPORTE BRUTO", descuentos: "DESCUENTOS/RECARGOS", neto: "IMPORTE NETO",
  medio: "MEDIO DE PAGO", forma: "FORMA DE PAGO", folio: "FOLIO DE PAGO", sucursal: "SUCURSAL", hora: "HORA", fecha: "FECHA DE PAGO",
};

function movimientosDeBanorte(tabla) {
  const I = indices(tabla[0], COLUMNAS_BANORTE, "archivo de pagos en ventanilla de Banorte");
  const movimientos = [];
  const omitidos = [];
  tabla.slice(1).forEach((crudo) => {
    // Banorte antepone un apostrofo a los numeros largos para que Excel no los recorte
    const r = crudo.map((v) => limpiar(v).replace(/^'/, ""));
    if (!r[I.folio]) return;
    const fecha = fechaDeDmy(r[I.fecha]);
    const bruto = numero(r[I.bruto]);
    const neto = numero(r[I.neto]);
    if (!fecha || Number.isNaN(neto)) { omitidos.push(`${r[I.fecha]} ${r[I.neto]}`); return; }
    // La referencia viene rellenada con ceros a la izquierda hasta 40 digitos; los ultimos 34 son la de Multipagos
    const referencia = r[I.ref1].length > 34 ? r[I.ref1].slice(-34) : r[I.ref1];
    movimientos.push({
      fuente: "BANORTE",
      clave: `B|${r[I.folio]}`,
      cuenta: r[I.factura],
      oficial: oficialDeReferencia(referencia),
      tipo: "PAGO",
      fecha,
      hora: horaDe24(r[I.hora]),
      fecha_deposito: fecha,
      importe: redondear(neto),
      comision: redondear(Number.isNaN(bruto) ? 0 : bruto - neto),
      iva: 0,
      depositado: redondear(neto),
      autorizacion: "",
      referencia,
      referencia_tecleada: "",
      folio: r[I.folio],
      medio: [r[I.medio], r[I.forma]].filter(Boolean).join(" / "),
      estatus_banco: "",
    });
  });
  return { movimientos, omitidos };
}

// ---------------------------------------------------------------- BBVA: estado de cuenta, Cuaderno 43 (.exp)
// Registros de ancho fijo: 11 = encabezado de la cuenta (un bloque por dia), 22 = movimiento, 23 = complemento del
// movimiento anterior, 33 = cierre del bloque. En el 22: pos. 11-16 fecha de operacion, 17-22 fecha valor,
// 28 = 1 cargo / 2 abono, 29-42 importe (2 decimales), 53-80 referencia ("CE" + referencia CIE en los cobros).
// En el 23 de un cobro CIE: pos. 35-41 convenio y 43-52 folio de BBVA.
function movimientosDeBbva(texto) {
  const lineas = String(texto || "").split(/\r?\n/);
  const movimientos = [];
  const omitidos = [];
  let cuenta = "";
  lineas.forEach((l, i) => {
    if (l.startsWith("11")) cuenta = l.substring(10, 20);
    if (!l.startsWith("22")) return;
    const sig = lineas[i + 1] && lineas[i + 1].startsWith("23") ? lineas[i + 1] : "";
    const fecha = fechaDeAammdd(l.substring(10, 16));
    const fechaValor = fechaDeAammdd(l.substring(16, 22));
    const abono = l.charAt(27) === "2";
    const importe = Number(l.substring(28, 42)) / 100;
    const ref = l.substring(52, 80).trim();
    if (!fecha || !Number.isFinite(importe)) { omitidos.push(`renglón ${i + 1} sin fecha o importe`); return; }
    const base = { fuente: "BBVA", cuenta, fecha, hora: "", fecha_deposito: fechaValor, autorizacion: "", referencia_tecleada: "", estatus_banco: "" };
    if (abono && ref.startsWith("CE")) {
      const referencia = ref.substring(2);
      const convenio = sig.substring(34, 41).trim();
      const folio = sig.substring(42, 52).trim();
      movimientos.push({
        ...base,
        clave: ["V", cuenta, fecha, folio, Math.round(importe * 100), referencia].join("|"),
        cuenta: convenio || cuenta,
        oficial: oficialDeReferencia(referencia),
        tipo: "PAGO",
        importe: redondear(importe),
        comision: 0,
        iva: 0,
        depositado: redondear(importe),
        referencia,
        folio,
        medio: `CIE ${convenio}`.trim(),
      });
      return;
    }
    // Comisiones que BBVA cobra a mes vencido ("COM ..." y su "IVA COM ..."): no son de un plantel, se muestran aparte
    const concepto = ref.toUpperCase();
    if (!abono && (concepto.startsWith("COM ") || concepto.startsWith("IVA COM"))) {
      const esIva = concepto.startsWith("IVA");
      movimientos.push({
        ...base,
        clave: ["V", cuenta, fecha, l.substring(22, 27).trim(), Math.round(importe * 100), concepto].join("|"),
        oficial: "",
        tipo: "COMISION",
        importe: 0,
        comision: esIva ? 0 : redondear(importe),
        iva: esIva ? redondear(importe) : 0,
        depositado: redondear(-importe),
        referencia: concepto,
        folio: "",
        medio: sig.substring(4, 42).trim(),
      });
      return;
    }
    // Traspasos entre cuentas (SPEI), intereses y otros: no son cobros de los planteles
    omitidos.push(`${fecha} ${abono ? "abono" : "cargo"} $${importe.toFixed(2)} ${concepto}`.trim());
  });
  if (!movimientos.length && !omitidos.length) throw new Error("El archivo de BBVA no trae movimientos.");
  return { movimientos, omitidos };
}

// ---------------------------------------------------------------- deteccion del tipo de archivo
export function tipoDeArchivo(nombre, texto) {
  const t = String(texto || "").replace(/^﻿/, "");
  if (/^11\d{18}/.test(t) && /\n22/.test(t)) return "BBVA";
  const primera = sinAcentos(t.split(/\r?\n/)[0] || "");
  if (primera.includes("total de comisiones") && primera.includes("afiliacion")) return "TARJETA";
  if (primera.includes("folio de pago") && primera.includes("referencia 1")) return "BANORTE";
  if (primera.includes("afiliacion") && primera.includes("estado")) {
    throw new Error(`"${nombre}" es el reporte "Transacciones Payworks 2". Aquí se usa el "Reporte de Depósitos" de MultiCobros (Analítica → Reportes → Reporte de depósitos), porque trae lo depositado.`);
  }
  throw new Error(`No reconozco "${nombre}". Debe ser el Reporte de Depósitos de MultiCobros (CSV), el archivo de pagos en ventanilla de Banorte (CSV) o el estado de cuenta de BBVA (.exp), tal como se descargan.`);
}

// texto del archivo -> { fuente, movimientos, omitidos, resumen }
export function leerArchivoBanco(nombre, texto) {
  const fuente = tipoDeArchivo(nombre, texto);
  const { movimientos, omitidos } = fuente === "BBVA"
    ? movimientosDeBbva(texto)
    : (fuente === "TARJETA" ? movimientosDeDepositos : movimientosDeBanorte)(parsearCsv(texto));
  if (!movimientos.length) throw new Error(`"${nombre}" no trae movimientos que guardar.`);
  // Una misma clave dos veces en un archivo seria un error de lectura: no se guarda nada
  const vistas = new Set();
  movimientos.forEach((m) => {
    if (vistas.has(m.clave)) throw new Error(`"${nombre}" trae dos veces el mismo movimiento (${m.clave}). Vuelve a descargarlo.`);
    vistas.add(m.clave);
  });
  const fechas = movimientos.map((m) => m.fecha).sort();
  const suma = (campo) => redondear(movimientos.reduce((s, m) => s + m[campo], 0));
  return {
    fuente,
    nombre,
    movimientos,
    omitidos,
    resumen: {
      movimientos: movimientos.length,
      desde: fechas[0],
      hasta: fechas[fechas.length - 1],
      importe: suma("importe"),
      depositado: suma("depositado"),
      sinPlantel: movimientos.filter((m) => !m.oficial && m.tipo !== "COMISION").length,
    },
  };
}

// El estado de cuenta de BBVA viene en Windows-1252; los CSV de los portales, en UTF-8
export const codificacionDe = (nombre) => (String(nombre || "").toLowerCase().endsWith(".exp") ? "windows-1252" : "utf-8");
